import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/authService';
import { moduleService } from '../services/moduleService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [moduleAccess, setModuleAccess] = useState({}); // { code: { ...ModuleDTO } }
  const [modulePermissions, setModulePermissions] = useState({}); // { moduleCode: { canView, canCreate, canUpdate, canDelete, canApprove } }

  useEffect(() => {
    // Vérifier si l'utilisateur est déjà connecté au chargement
    const token = authService.getToken();
    const savedUser = authService.getUser();
    
    const init = async () => {
      if (token && savedUser) {
        setUser(savedUser);
        setIsAuthenticated(true);
        try {
          const res = await moduleService.getAccessibleModules();
          const mods = (res.data || res) ?? [];
          const byCode = {};
          mods.forEach((m) => {
            if (m.code) {
              byCode[m.code] = m;
            }
          });
          setModuleAccess(byCode);

          // Charger les permissions détaillées
          try {
            const permRes = await moduleService.getMyPermissions();
            const perms = (permRes.data || permRes) ?? [];
            const permsByCode = {};
            perms.forEach((p) => {
              if (p.moduleCode) {
                permsByCode[p.moduleCode] = {
                  canView: p.canView || false,
                  canCreate: p.canCreate || false,
                  canUpdate: p.canUpdate || false,
                  canDelete: p.canDelete || false,
                  canApprove: p.canApprove || false,
                };
              }
            });
            setModulePermissions(permsByCode);
          } catch (e) {
            console.error('Erreur lors du chargement des permissions détaillées', e);
          }
        } catch (e) {
          console.error('Erreur lors du chargement des modules accessibles', e);
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await authService.login(username, password);
      const userData = {
        id: response.id,
        username: response.username,
        email: response.email,
        roles: response.roles,
      };
      
      authService.setAuthData(response.token, userData);
      setUser(userData);
      setIsAuthenticated(true);

      // Charger les modules accessibles et les permissions détaillées
      try {
        const res = await moduleService.getAccessibleModules();
        const mods = (res.data || res) ?? [];
        const byCode = {};
        mods.forEach((m) => {
          if (m.code) {
            byCode[m.code] = m;
          }
        });
        setModuleAccess(byCode);

        // Charger les permissions détaillées
        try {
          const permRes = await moduleService.getMyPermissions();
          const perms = (permRes.data || permRes) ?? [];
          const permsByCode = {};
          perms.forEach((p) => {
            if (p.moduleCode) {
              permsByCode[p.moduleCode] = {
                canView: p.canView || false,
                canCreate: p.canCreate || false,
                canUpdate: p.canUpdate || false,
                canDelete: p.canDelete || false,
                canApprove: p.canApprove || false,
              };
            }
          });
          setModulePermissions(permsByCode);
        } catch (e) {
          console.error('Erreur lors du chargement des permissions détaillées', e);
        }
      } catch (e) {
        console.error('Erreur lors du chargement des modules accessibles', e);
      }
      return { success: true, user: userData };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erreur de connexion';
      return { success: false, error: errorMessage };
    }
  };

  const signup = async (userData) => {
    try {
      await authService.signup(userData);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
        (error.response?.data && typeof error.response.data === 'object' 
          ? Object.values(error.response.data).join(', ') 
          : 'Erreur lors de l\'inscription');
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setModuleAccess({});
    setModulePermissions({});
  };

  const hasRole = (role) => {
    if (!user || !user.roles) return false;
    return user.roles.includes(role);
  };

  const hasAnyRole = (...roles) => {
    if (!user || !user.roles) return false;
    return roles.some(role => user.roles.includes(role));
  };

  const hasAllRoles = (...roles) => {
    if (!user || !user.roles) return false;
    return roles.every(role => user.roles.includes(role));
  };

  const hasPermission = (permission) => {
    if (!user || !user.roles) return false;
    
    // Mapping des rôles aux permissions
    const rolePermissions = {
      'ROLE_RH': [
        'EMPLOYEE_VIEW', 'EMPLOYEE_CREATE', 'EMPLOYEE_UPDATE', 'EMPLOYEE_DELETE',
        'LEAVE_VIEW', 'LEAVE_CREATE', 'LEAVE_APPROVE_MANAGER', 'LEAVE_APPROVE_RH', 'LEAVE_DELETE',
        'PAYROLL_VIEW', 'PAYROLL_CREATE', 'PAYROLL_UPDATE', 'PAYROLL_DELETE',
        'PAYROLL_VALIDATE', 'PAYROLL_DOWNLOAD', 'PAYROLL_SEND_EMAIL',
        'DOCUMENT_VIEW', 'DOCUMENT_UPLOAD', 'DOCUMENT_DELETE',
        'TIMESHEET_VIEW', 'TIMESHEET_VIEW_ALL', 'TIMESHEET_CREATE', 'TIMESHEET_UPDATE',
        'TIMESHEET_DELETE', 'TIMESHEET_APPROVE', 'TIMESHEET_REJECT',
        'AUDIT_VIEW',
        'USER_VIEW', 'USER_CREATE', 'USER_UPDATE', 'USER_DELETE'
      ],
      'ROLE_MANAGER': [
        'EMPLOYEE_VIEW',
        'LEAVE_VIEW', 'LEAVE_CREATE', 'LEAVE_APPROVE_MANAGER',
        'PAYROLL_VIEW', 'PAYROLL_DOWNLOAD',
        'DOCUMENT_VIEW', 'DOCUMENT_UPLOAD',
        'TIMESHEET_VIEW', 'TIMESHEET_VIEW_ALL', 'TIMESHEET_CREATE', 'TIMESHEET_UPDATE'
      ],
      'ROLE_USER': [
        'LEAVE_VIEW', 'LEAVE_CREATE',
        'PAYROLL_VIEW', 'PAYROLL_DOWNLOAD',
        'DOCUMENT_VIEW', 'DOCUMENT_UPLOAD',
        'TIMESHEET_VIEW', 'TIMESHEET_CREATE', 'TIMESHEET_UPDATE'
      ]
    };

    // Vérifier si l'utilisateur a la permission via ses rôles
    if (user.roles) {
      for (const role of user.roles) {
        const permissions = rolePermissions[role] || [];
        if (permissions.includes(permission)) {
          return true;
        }
      }
    }

    return false;
  };

  const hasModuleAccess = (moduleCode) => {
    if (!moduleCode) return false;
    if (moduleAccess && moduleAccess[moduleCode]) {
      return true;
    }
    // Fallback : logique précédente basée sur les rôles pour compat
    if (!user || !user.roles) return false;
    return user.roles.includes('ROLE_RH') || user.roles.includes('ROLE_MANAGER');
  };

  const hasModulePermission = (moduleCode, permission) => {
    if (!moduleCode || !permission) return false;
    if (!user || !user.roles) return false;
    
    // Vérifier les permissions explicites
    if (modulePermissions && modulePermissions[moduleCode]) {
      const perm = modulePermissions[moduleCode];
      const p = permission.toLowerCase();
      if (p === 'view') return perm.canView;
      if (p === 'create') return perm.canCreate;
      if (p === 'update') return perm.canUpdate;
      if (p === 'delete') return perm.canDelete;
      if (p === 'approve') return perm.canApprove;
    }
    
    // Fallback : vérifier les rôles
    // ROLE_RH : toutes les permissions sur tous les modules
    if (user.roles.includes('ROLE_RH')) {
      return true;
    }
    
    // ROLE_MANAGER : permissions limitées
    if (user.roles.includes('ROLE_MANAGER')) {
      const managerModules = ['DASHBOARD', 'EMPLOYEES', 'LEAVES', 'HOLIDAYS', 'TIMESHEETS', 'PAYROLLS', 'SELF_SERVICE'];
      if (managerModules.includes(moduleCode)) {
        if (permission.toLowerCase() === 'view') return true;
        if (moduleCode === 'LEAVES' && permission.toLowerCase() === 'approve') return true;
        if (moduleCode === 'TIMESHEETS' && (permission.toLowerCase() === 'create' || permission.toLowerCase() === 'update')) return true;
        if (moduleCode === 'EMPLOYEES' && permission.toLowerCase() === 'view') return true;
        if (moduleCode === 'PAYROLLS' && permission.toLowerCase() === 'view') return true;
      }
    }
    
    // ROLE_USER : permissions très limitées
    if (user.roles.includes('ROLE_USER')) {
      const userModules = ['DASHBOARD', 'LEAVES', 'PAYROLLS', 'SELF_SERVICE'];
      if (userModules.includes(moduleCode)) {
        if (permission.toLowerCase() === 'view') return true;
        if (moduleCode === 'LEAVES' && permission.toLowerCase() === 'create') return true;
      }
    }
    
    return false;
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    signup,
    logout,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasPermission,
    hasModuleAccess,
    hasModulePermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

